/**
 * ============================================================================
 * ADMIN SERVICE - API CLIENT
 * ============================================================================
 * 
 * Handles all admin-related API calls
 * Note: All endpoints require admin authentication
 */

import api, { handleApiError } from './api';

/**
 * Get dashboard metrics
 */
export const getDashboardMetrics = async (filters = {}) => {
  try {
    const response = await api.get('/admin/dashboard', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get analytics data
 */
export const getAnalytics = async (filters = {}) => {
  try {
    const response = await api.get('/admin/analytics', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List transactions (admin)
 */
export const listTransactions = async (filters = {}) => {
  try {
    const response = await api.get('/admin/transactions', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get transaction details (admin)
 */
export const getTransactionDetails = async (transactionId) => {
  try {
    const response = await api.get(`/admin/transactions/${transactionId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Cancel transaction (admin)
 */
export const cancelTransaction = async (transactionId, data) => {
  try {
    const response = await api.post(`/admin/transactions/${transactionId}/cancel`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List disputes (admin)
 */
export const listDisputes = async (filters = {}) => {
  try {
    const response = await api.get('/admin/disputes', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dispute details (admin)
 */
export const getDisputeDetails = async (disputeId) => {
  try {
    const response = await api.get(`/admin/disputes/${disputeId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Start mediation (admin)
 */
export const startMediation = async (disputeId, data = {}) => {
  try {
    const response = await api.post(`/admin/disputes/${disputeId}/start-mediation`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Resolve dispute (admin)
 */
export const resolveDispute = async (disputeId, data) => {
  try {
    const response = await api.post(`/admin/disputes/${disputeId}/resolve`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Add dispute note (admin)
 */
export const addDisputeNote = async (disputeId, data) => {
  try {
    const response = await api.post(`/admin/disputes/${disputeId}/notes`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List users (admin)
 */
export const listUsers = async (filters = {}) => {
  try {
    const response = await api.get('/admin/users', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user details (admin)
 */
export const getUserDetails = async (userId) => {
  try {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Suspend user (admin)
 */
export const suspendUser = async (userId, data) => {
  try {
    const response = await api.post(`/admin/users/${userId}/suspend`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Ban user (admin)
 */
export const banUser = async (userId, data) => {
  try {
    const response = await api.post(`/admin/users/${userId}/ban`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Reactivate user (admin)
 */
export const reactivateUser = async (userId) => {
  try {
    const response = await api.post(`/admin/users/${userId}/reactivate`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Verify user (admin)
 */
export const verifyUser = async (userId, data) => {
  try {
    const response = await api.post(`/admin/users/${userId}/verify`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get financial overview (admin)
 */
export const getFinancialOverview = async (filters = {}) => {
  try {
    const response = await api.get('/admin/financials', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List withdrawals (admin)
 */
export const listWithdrawals = async (filters = {}) => {
  try {
    const response = await api.get('/admin/withdrawals', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Approve withdrawal (admin)
 */
export const approveWithdrawal = async (withdrawalId, data) => {
  try {
    const response = await api.post(`/admin/withdrawals/${withdrawalId}/process`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Reject withdrawal (admin)
 */
export const rejectWithdrawal = async (withdrawalId, data) => {
  try {
    const response = await api.post(`/admin/withdrawals/${withdrawalId}/reject`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get system settings (admin)
 */
export const getSettings = async () => {
  try {
    const response = await api.get('/admin/settings');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update system settings (admin)
 */
export const updateSettings = async (data) => {
  try {
    const response = await api.patch('/admin/settings', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get audit logs (admin)
 */
export const getAuditLogs = async (filters = {}) => {
  try {
    const response = await api.get('/admin/audit-logs', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get admin profile
 */
export const getAdminProfile = async () => {
  try {
    const response = await api.get('/admin/profile');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get admin activity
 */
export const getAdminActivity = async () => {
  try {
    const response = await api.get('/admin/activity');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get system health
 */
export const getSystemHealth = async () => {
  try {
    const response = await api.get('/admin/system-health');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Clear cache (super admin only)
 */
export const clearCache = async () => {
  try {
    const response = await api.post('/admin/clear-cache');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Run reconciliation (super admin only)
 */
export const runReconciliation = async () => {
  try {
    const response = await api.post('/admin/run-reconciliation');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update system setting
 */
export const updateSystemSetting = async (key, value) => {
  try {
    const response = await api.put(`/admin/settings/${key}`, { value });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List all notifications (admin)
 */
export const listAllNotifications = async (filters = {}) => {
  try {
    const response = await api.get('/admin/notifications', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Mark notification as read (admin)
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await api.post(`/admin/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete notification (admin)
 */
export const deleteNotification = async (notificationId) => {
  try {
    const response = await api.delete(`/admin/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Send notification to user(s) (admin)
 */
export const sendNotification = async (data) => {
  try {
    const response = await api.post('/admin/notifications/send', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List all escrows (admin)
 */
export const listAllEscrows = async (filters = {}) => {
  try {
    const response = await api.get('/escrows/admin/all', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get escrow details (admin)
 */
export const getEscrowDetails = async (escrowId) => {
  try {
    const response = await api.get(`/escrows/${escrowId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Admin partial release escrow
 */
export const adminPartialRelease = async (escrowId, data) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/partial-release`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Admin refund escrow
 */
export const adminRefundEscrow = async (escrowId, data) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/refund`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List all payments (admin)
 */
export const listAllPayments = async (filters = {}) => {
  try {
    const response = await api.get('/payments/admin/all', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get payment details (admin)
 */
export const getPaymentDetails = async (paymentId) => {
  try {
    const response = await api.get(`/payments/${paymentId}/status`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Retry failed payment (admin)
 */
export const retryPayment = async (paymentId, data = {}) => {
  try {
    const response = await api.post(`/payments/${paymentId}/retry`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Refund payment (admin)
 */
export const refundPayment = async (paymentId, data = {}) => {
  try {
    const response = await api.post(`/payments/${paymentId}/refund`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  getDashboardMetrics,
  getAnalytics,
  listTransactions,
  getTransactionDetails,
  cancelTransaction,
  listDisputes,
  getDisputeDetails,
  startMediation,
  resolveDispute,
  addDisputeNote,
  listUsers,
  getUserDetails,
  suspendUser,
  banUser,
  reactivateUser,
  verifyUser,
  getFinancialOverview,
  listWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
  getSettings,
  updateSettings,
  updateSystemSetting,
  getAuditLogs,
  getAdminProfile,
  getAdminActivity,
  getSystemHealth,
  clearCache,
  runReconciliation,
  listAllNotifications,
  markNotificationAsRead,
  deleteNotification,
  sendNotification,
  listAllEscrows,
  getEscrowDetails,
  adminPartialRelease,
  adminRefundEscrow,
  listAllPayments,
  getPaymentDetails,
  retryPayment,
  refundPayment,
};
